import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Container, Header, Segment, Button, Loader, Dimmer, Message, Icon, Input, Label,
} from 'semantic-ui-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import moment from 'moment';
import { getEventDetail, getConferenceDetail, createOrder, createPaymentIntent, confirmPayment } from './lib/tickets';
import { getConfiguration, loadStripeForConfig } from './lib/configuration';
import { getUser } from './lib/authentication';
import { buildPath } from './lib/tenant';
import { track } from './lib/telemetry';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function CheckoutForm({ order, displayAmount, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handlePay = async () => {
        if (!stripe || !elements) return;
        setProcessing(true);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        });

        if (error) {
            onError(error.message);
            setProcessing(false);
            return;
        }

        if (paymentIntent?.status === 'succeeded') {
            await confirmPayment({
                orderRef: order.order_ref,
                paymentIntentId: paymentIntent.id,
            });
            onSuccess(order.order_ref);
        } else {
            onError(t('checkout.paymentFailed'));
            setProcessing(false);
        }
    };

    return (
        <div>
            <PaymentElement />
            <Button
                fluid
                size="large"
                loading={processing}
                disabled={processing || !stripe}
                onClick={handlePay}
                style={{
                    marginTop: '1.5rem',
                    backgroundColor: 'var(--colour_menu_main)',
                    color: '#fff',
                    borderRadius: 8,
                }}
            >
                {t('checkout.payButton', { amount: formatMoney(displayAmount, order.currency) })}
            </Button>
        </div>
    );
}

function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const instanceId = parseInt(params.get('instanceId'), 10);
    const quantity = parseInt(params.get('qty'), 10) || 1;
    const eventId = params.get('eventId');
    const conferenceId = params.get('conferenceId');
    const isConference = !!conferenceId;

    const [order, setOrder] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [paymentError, setPaymentError] = useState(null);
    const [initialising, setInitialising] = useState(true);
    const [initError, setInitError] = useState(null);
    const [proceedError, setProceedError] = useState(null);

    // Coupon state
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState(null);
    const [discountAmount, setDiscountAmount] = useState(null);
    const [finalAmount, setFinalAmount] = useState(null);

    // Two-phase: 'summary' | 'payment'
    const [phase, setPhase] = useState('summary');
    const [proceedingToPayment, setProceedingToPayment] = useState(false);

    const configQuery = useQuery({
        queryKey: ['configuration'],
        queryFn: getConfiguration,
        staleTime: 60 * 60 * 1000,
    });

    const { data: eventData } = useQuery({
        queryKey: ['ticketing-event', eventId],
        queryFn: () => getEventDetail(eventId),
        enabled: !!eventId && !isConference,
    });

    const { data: conferenceData } = useQuery({
        queryKey: ['ticketing-conference', conferenceId],
        queryFn: () => getConferenceDetail(conferenceId),
        enabled: isConference,
    });

    const itemData = isConference ? conferenceData : eventData;

    // On mount: resolve user identity and load the Stripe key.
    // Order creation is deferred to "Proceed to Payment" so that browsing the
    // checkout page without completing payment does not create stale pending orders.
    useEffect(() => {
        if (!isConference && !instanceId) {
            setInitError(t('checkout.invalidLink'));
            setInitialising(false);
            return;
        }

        const init = async () => {
            try {
                const user = await getUser();
                if (!user) {
                    navigate(buildPath(`/login?next=${encodeURIComponent(buildPath('/checkout') + location.search)}`));
                    return;
                }
                setUserEmail(user.email);

                if (configQuery.data?.stripe_api_key || configQuery.data?.stripe_connect_account_id) {
                    setStripePromise(loadStripeForConfig(configQuery.data));
                }
            } catch (e) {
                setInitError(t('checkout.initError'));
            } finally {
                setInitialising(false);
            }
        };

        if (!configQuery.isLoading) {
            init();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configQuery.isLoading]);

    const handleProceedToPayment = async () => {
        setProceedingToPayment(true);
        setCouponError(null);
        setProceedError(null);
        try {
            // Read referral code captured by EventDetail when the page was visited via a share link
            const referredBy = sessionStorage.getItem('ticketing_referral') || undefined;

            // Create the order now — deferred from mount so abandoned checkouts
            // don't leave stale pending orders in the database.
            const createdOrder = await createOrder(
                isConference
                    ? { conferenceId, quantity, referredBy }
                    : { eventInstanceId: instanceId, quantity, referredBy }
            );
            if (createdOrder.error) {
                setProceedError(createdOrder.error);
                return;
            }
            // Clear referral after successful order creation so it doesn't apply to future orders
            if (referredBy) sessionStorage.removeItem('ticketing_referral');
            setOrder(createdOrder);
            setFinalAmount(createdOrder.total_amount);
            track('checkout_started', '/checkout', { order_ref: createdOrder.order_ref, quantity });

            const intent = await createPaymentIntent({
                price: createdOrder.total_amount,
                email: userEmail,
                couponCode: couponCode.trim() || undefined,
                orderRef: createdOrder.order_ref,
            });

            if (!intent.data?.client_secret) {
                // API returns { success: false, data: "error string" } on failure
                const errMsg = typeof intent.data === 'string'
                    ? intent.data
                    : (intent.data?.error ?? intent.error ?? '');
                if (couponCode.trim() && errMsg.toLowerCase().includes('coupon')) {
                    setCouponError(errMsg || t('checkout.couponInvalid'));
                } else {
                    setProceedError(errMsg || t('checkout.proceedErrorInit'));
                }
                return;
            }

            if (intent.data.final_price != null) {
                setFinalAmount(intent.data.final_price);
                const raw = parseFloat(createdOrder.total_amount);
                const final = parseFloat(intent.data.final_price);
                if (final < raw) setDiscountAmount((raw - final).toFixed(2));
            }

            setClientSecret(intent.data.client_secret);
            setPhase('payment');
        } catch (e) {
            setProceedError(t('checkout.proceedErrorGeneric'));
        } finally {
            setProceedingToPayment(false);
        }
    };

    if (initialising || configQuery.isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('checkout.loading')} />
            </Dimmer>
        );
    }

    if (initError) {
        return (
            <Container className="contentContainer" style={{ maxWidth: 600 }}>
                <Message negative>
                    <Message.Header>{t('checkout.errorTitle')}</Message.Header>
                    <p>{initError}</p>
                </Message>
                <Button onClick={() => navigate(-1)}>{t('checkout.goBack')}</Button>
            </Container>
        );
    }

    // Find selected instance details for richer summary (event checkout only)
    const selectedInstance = !isConference ? eventData?.instances?.find(i => i.id === instanceId) : null;
    // The chosen date's own price (what create-order charges), else the series'
    const shownPrice = selectedInstance?.display_price ?? itemData?.display_price ?? itemData?.ticket_price ?? 0;

    return (
        <Container className="contentContainer" style={{ maxWidth: 600 }}>
            <Button
                basic
                icon="arrow left"
                content={t('checkout.back')}
                onClick={() => navigate(-1)}
                style={{ marginBottom: '1.5rem' }}
            />

            <Header as="h2" className="sectionHeader">{t('checkout.title')}</Header>

            {/* Order summary — visible as soon as item data is available */}
            {itemData && (
                <Segment style={{ borderRadius: 12, marginBottom: '1.5rem', background: '#f9f9f9' }}>
                    <p style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--colour_menu_main)', marginBottom: '0.75rem' }}>
                        {itemData.name}
                    </p>
                    {isConference && itemData.start_date && (
                        <p style={{ margin: '0 0 0.25rem', color: '#555' }}>
                            <Icon name="calendar alternate outline" />
                            {moment(itemData.start_date).format('dddd D MMMM YYYY')}
                        </p>
                    )}
                    {selectedInstance?.date_time && (
                        <p style={{ margin: '0 0 0.25rem', color: '#555' }}>
                            <Icon name="calendar alternate outline" />
                            {moment(selectedInstance.date_time).format('dddd D MMMM YYYY, HH:mm')}
                        </p>
                    )}
                    {(selectedInstance?.location || itemData?.location) && (
                        <p style={{ margin: '0 0 0.75rem', color: '#555' }}>
                            <Icon name="map marker alternate" />
                            {selectedInstance?.location || itemData?.location}
                        </p>
                    )}

                    <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
                        <p style={{ margin: 0 }}>
                            <Icon name={isConference ? 'id badge' : 'ticket'} />
                            <strong>
                                {isConference
                                    ? t('checkout.conferencePass')
                                    : t('checkout.ticketsCount', { count: quantity, plural: quantity > 1 ? 's' : '' })}
                            </strong>
                        </p>
                        <p style={{ margin: '0.4rem 0 0', color: '#555' }}>
                            {formatMoney(order?.unit_price ?? shownPrice, order?.currency)}{!isConference ? ` × ${quantity}` : ''}
                        </p>
                        {discountAmount && (
                            <p style={{ margin: '0.4rem 0 0', color: 'var(--colour_heading_success)', fontWeight: 600 }}>
                                <Icon name="tag" />
                                {t('checkout.couponDiscount', { amount: formatMoney(discountAmount, order?.currency) })}
                            </p>
                        )}
                        <p style={{ margin: '0.75rem 0 0', fontSize: '1.15rem', fontWeight: 700, color: 'var(--colour_menu_main)' }}>
                            {t('checkout.total', { amount: formatMoney(finalAmount ?? order?.total_amount ?? (shownPrice * quantity), order?.currency) })}
                        </p>
                        {order?.order_ref && (
                            <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#888' }}>
                                {t('checkout.orderRefLabel')} {order.order_ref}
                            </p>
                        )}
                    </div>
                </Segment>
            )}

            {/* Phase 1: coupon + proceed */}
            {phase === 'summary' && (
                <Segment style={{ borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' }}>
                    <Header as="h4" style={{ marginBottom: '1rem', color: 'var(--colour_menu_main)' }}>
                        {t('checkout.couponTitle')}
                    </Header>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: couponError ? '0.4rem' : '1.5rem' }}>
                        <Input
                            placeholder={t('checkout.couponPlaceholder')}
                            value={couponCode}
                            onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                            style={{ flex: 1 }}
                        />
                    </div>
                    {couponError && (
                        <Message negative size="small" style={{ marginBottom: '1rem' }}>
                            {couponError}
                        </Message>
                    )}
                    {proceedError && (
                        <Message negative size="small" style={{ marginBottom: '1rem' }}>
                            {proceedError}
                        </Message>
                    )}
                    <Button
                        fluid
                        size="large"
                        loading={proceedingToPayment}
                        disabled={proceedingToPayment}
                        onClick={handleProceedToPayment}
                        style={{
                            backgroundColor: 'var(--colour_menu_main)',
                            color: '#fff',
                            borderRadius: 8,
                        }}
                    >
                        {t('checkout.proceedToPayment')}
                    </Button>
                </Segment>
            )}

            {/* Phase 2: Stripe payment */}
            {phase === 'payment' && (
                <>
                    {paymentError && (
                        <Message negative style={{ marginBottom: '1rem' }}>
                            {paymentError}
                        </Message>
                    )}

                    {clientSecret && stripePromise && order ? (
                        <Segment style={{ borderRadius: 12, padding: '2rem' }}>
                            <Header as="h4" style={{ marginBottom: '1.5rem', color: 'var(--colour_menu_main)' }}>
                                {t('checkout.paymentDetails')}
                            </Header>
                            {discountAmount && (
                                <Label
                                    style={{
                                        backgroundColor: 'var(--colour_heading_success)',
                                        color: '#fff',
                                        marginBottom: '1rem',
                                        display: 'inline-block',
                                    }}
                                >
                                    <Icon name="tag" />
                                    {t('checkout.couponApplied', { amount: formatMoney(discountAmount, order?.currency) })}
                                </Label>
                            )}
                            <Elements
                                stripe={stripePromise}
                                options={{ clientSecret, appearance: { theme: 'stripe' } }}
                            >
                                <CheckoutForm
                                    order={order}
                                    displayAmount={finalAmount ?? order.total_amount}
                                    onSuccess={(ref) => { track('payment_confirmed', '/checkout', { order_ref: ref }); navigate(buildPath(`/order-confirmation/${ref}`)); }}
                                    onError={(msg) => setPaymentError(msg)}
                                />
                            </Elements>
                        </Segment>
                    ) : (
                        <Message warning>
                            {t('checkout.paymentNotConfigured')}
                        </Message>
                    )}
                </>
            )}
        </Container>
    );
}

export default Checkout;
